import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminRoutes } from "../../../routers/AdminRouters";
import { useAppSelector } from "../../../redux/hooks";
import { RootState } from "../../../redux/store";
import { useTheme } from "../../../contexts/ThemeContext";
import {
    useGetTiersQuery,
    useDeleteTierMutation,
} from "../../../services";
import { ITier } from "../../../types";

const TiersList: React.FC = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();

    // Load from RTK
    const {
        data: tiersResponse,
        isLoading,
        isFetching,
        error,
    } = useGetTiersQuery();
    const [deleteTier, { isLoading: isDeleting }] = useDeleteTierMutation();

    const [deleteTarget, setDeleteTarget] = useState<ITier | null>(null);
    const openDelete = (row: ITier) => setDeleteTarget(row);
    const closeDelete = () => setDeleteTarget(null);

    const confirmDelete = async () => {
        if (!deleteTarget?._id) return;
        try {
            await deleteTier(deleteTarget._id).unwrap();
        } catch (e) {
            console.error("Delete Tier failed:", e);
        } finally {
            closeDelete();
        }
    };

    const rows: ITier[] = (tiersResponse ?? []);

    // THEME CLASSES
    const pageBg = theme === "dark" ? "bg-[#0A0A0A]" : "bg-gray-50";
    const heading = "text-text";
    const crumbLink = theme === "dark" ? "text-gray-500" : "text-gray-500";
    const crumbSep = theme === "dark" ? "text-gray-600" : "text-gray-400";

    const cardClass =
        theme === "dark"
            ? "bg-[#1A1A1A] border-0 text-gray-100"
            : "bg-white border border-gray-200 text-gray-900";

    const cardHeader =
        theme === "dark"
            ? "px-6 py-4 border-b border-[#2A2A2A] text-white flex items-center justify-between"
            : "px-6 py-4 border-b border-gray-200 text-gray-800 flex items-center justify-between";

    const thClass =
        theme === "dark"
            ? "px-6 py-3 text-left text-sm font-bold text-[#F3F3F6] tracking-wider border-b border-[#2A2A2A]"
            : "px-6 py-3 text-left text-sm font-bold text-gray-700 tracking-wider border-b border-gray-200";

    const tdClass =
        theme === "dark"
            ? "px-6 py-4 text-sm text-text-table border-b border-[#2A2A2A]"
            : "px-6 py-4 text-sm text-text-table border-b border-gray-200";

    const iconHover = theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5";

    const isSidebarOpen = useAppSelector(
        (state: RootState) => state.navbar.isOpen
    );
    const sidebarWidth = isSidebarOpen ? 256 : 64;

    return (
        <div
            className={`px-4 md:px-6 lg:px-8 pt-20 pb-8 min-h-screen ${pageBg}`}
            style={{
                marginLeft: sidebarWidth,
                width: `calc(100% - ${sidebarWidth}px)`,
            }}
        >
            {/* Breadcrumb + Title */}
            <div className="mb-6">
                <h1 className={`text-lg font-medium ${heading} mb-2`}>
                    Tiers Overview
                </h1>
                <div className="text-sm flex items-center gap-1">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                    >
                        <path
                            d="M3 11.9896V14.5C3 17.7998 3 19.4497 4.02513 20.4749C5.05025 21.5 6.70017 21.5 10 21.5H14C17.2998 21.5 18.9497 21.5 19.9749 20.4749C21 19.4497 21 17.7998 21 14.5V11.9896C21 10.3083 21 9.46773 20.6441 8.74005C20.2882 8.01237 19.6247 7.49628 18.2976 6.46411L16.2976 4.90855C14.2331 3.30285 13.2009 2.5 12 2.5C10.7991 2.5 9.76689 3.30285 7.70242 4.90855L5.70241 6.46411C4.37533 7.49628 3.71179 8.01237 3.3559 8.74005C3 9.46773 3 10.3083 3 11.9896Z"
                            stroke="#ADADAD"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M15 21.5V16.5C15 15.0858 15 14.3787 14.5607 13.9393C14.1213 13.5 13.4142 13.5 12 13.5C10.5858 13.5 9.87868 13.5 9.43934 13.9393C9 14.3787 9 15.0858 9 16.5V21.5"
                            stroke="#ADADAD"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <Link
                        to={adminRoutes.dashboard}
                        className={`${crumbLink} hover:text-[#B6CB24] transition-colors`}
                    >
                        Dashboard
                    </Link>
                    <span className={crumbSep}>&gt;</span>
                    <span className="text-[#B6CB24] font-normal">All Tiers</span>
                </div>
            </div>

            {/* Card */}
            <div className={`${cardClass} rounded-lg overflow-hidden shadow-lg`}>
                <div className={cardHeader}>
                    <span className="text-base font-medium">All Tier details</span>

                    <button
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#B6CB24] text-[#B6CB24] hover:bg-[#B6CB24]/10 text-sm font-medium"
                        onClick={() => navigate(adminRoutes.tiersCreate)}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                        >
                            <path
                                d="M12 2.00012C17.5228 2.00012 22 6.47727 22 12.0001C22 17.523 17.5228 22.0001 12 22.0001C6.47715 22.0001 2 17.523 2 12.0001M8.909 2.48699C7.9 2.8146 6.96135 3.29828 6.12153 3.90953M3.90943 6.12162C3.29806 6.9616 2.81432 7.90044 2.4867 8.90964"
                                stroke="#F3F3F6"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M12 8.00012V16.0001M16 12.0001H8"
                                stroke="#C6D92C"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                        Create new Tier
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr>
                                <th className={thClass}>Name</th>
                                <th className={thClass}>Description</th>
                                <th className={thClass}>Status</th>
                                <th className={thClass}>Actions</th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading || isFetching ? (
                                <tr>
                                    <td className={`${tdClass} text-center`} colSpan={4}>
                                        Loading tiers...
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td className={`${tdClass} text-center`} colSpan={4}>
                                        Failed to load tiers.
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                <tr>
                                    <td className={`${tdClass} text-center`} colSpan={4}>
                                        No tiers found.
                                    </td>
                                </tr>
                            ) : (
                                rows.map((r) => (
                                    <tr
                                        key={r._id}
                                        className={
                                            theme === "dark"
                                                ? "hover:bg-[#222222]"
                                                : "hover:bg-gray-50"
                                        }
                                    >
                                        <td className={tdClass}>{r.name}</td>
                                        <td className={tdClass}>{r.description}</td>
                                        <td className={tdClass}>
                                            <span className={`px-2 py-1 rounded text-xs font-medium ${r.status === "Active"
                                                ? "bg-lime-500/10 text-lime-500"
                                                : "bg-red-500/10 text-red-500"
                                                }`}>
                                                {r.status}
                                            </span>
                                        </td>
                                        <td className={tdClass}>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    title="Edit"
                                                    className={`p-1.5 rounded transition-colors ${iconHover}`}
                                                    onClick={() =>
                                                        navigate(
                                                            adminRoutes.tiersEdit.replace(
                                                                ":tierid",
                                                                r._id
                                                            )
                                                        )
                                                    }
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="24"
                                                        height="24"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M16.4249 4.60509L17.4149 3.6151C18.2351 2.79497 19.5648 2.79497 20.3849 3.6151C21.205 4.43524 21.205 5.76493 20.3849 6.58507L19.3949 7.57506M16.4249 4.60509L9.76558 11.2644C9.25807 11.772 8.89804 12.4078 8.72397 13.1041L8 16L10.8959 15.276C11.5922 15.102 12.228 14.7419 12.7356 14.2344L19.3949 7.57506M16.4249 4.60509L19.3949 7.57506"
                                                            stroke="#C6D92C"
                                                            strokeWidth="1.5"
                                                            strokeLinejoin="round"
                                                        />
                                                        <path
                                                            d="M18.9999 13.5C18.9999 16.7875 18.9999 18.4312 18.092 19.5376C17.9258 19.7401 17.7401 19.9258 17.5375 20.092C16.4312 21 14.7874 21 11.4999 21H11C7.22876 21 5.34316 21 4.17159 19.8284C3.00003 18.6569 3 16.7712 3 13V12.5C3 9.21252 3 7.56879 3.90794 6.46244C4.07417 6.2599 4.2599 6.07417 4.46244 5.90794C5.56879 5 7.21252 5 10.5 5"
                                                            stroke="#F3F3F6"
                                                            strokeWidth="1.5"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>
                                                <button
                                                    title="Delete"
                                                    className={`p-1.5 rounded transition-colors ${iconHover}`}
                                                    onClick={() => openDelete(r)}
                                                    disabled={isDeleting}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        width="20"
                                                        height="20"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
                                                            stroke="#ef4444"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Modal */}
            {deleteTarget && (
                <div className={`fixed inset-0 z-50 flex items-center justify-center p-4 ${theme === "dark" ? "bg-black/60" : "bg-black/40"}`}>
                    <div className={`relative w-full max-w-md rounded-2xl shadow-xl border ${theme === "dark" ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]" : "bg-white text-gray-900 border-gray-200"}`}>
                        <button
                            onClick={closeDelete}
                            className={`absolute top-3 right-3 p-2 rounded-full ${theme === "dark" ? "hover:bg-white/10" : "hover:bg-black/5"}`}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.29 19.7 2.88 18.29 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.29z" />
                            </svg>
                        </button>
                        <div className="px-6 py-6 text-center">
                            <h3 className="text-lg font-semibold mb-2">Delete Tier</h3>
                            <p className={`${theme === "dark" ? "text-gray-300" : "text-gray-600"} mb-6`}>
                                Are you sure you want to delete "{deleteTarget.name}"?
                            </p>
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? "Deleting..." : "Delete"}
                                </button>
                                <button
                                    onClick={closeDelete}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                                    disabled={isDeleting}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TiersList;
